/**
 * Пиктограммы нижнего меню.
 *
 * Требование дизайна: только линейные, обводка 1.5, размер 24, без заливок.
 * Свои SVG, а не библиотека: в наборе восемь иконок, а любая библиотека
 * притащит килобайты ради них.
 */

type IconProps = { className?: string };

const base = {
  width: 24,
  height: 24,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.5,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
};

/** Семь дней — календарь. */
export function IconCalendar({ className }: IconProps) {
  return (
    <svg {...base} className={className}>
      <rect x="3" y="5" width="18" height="16" rx="3" />
      <path d="M8 3v4M16 3v4M3 10h18" />
    </svg>
  );
}

/** Тридцать дней — динамика: путь длиннее, и он про изменение. */
export function IconChart({ className }: IconProps) {
  return (
    <svg {...base} className={className}>
      <path d="M4 19V9M10 19V5M16 19v-6M22 19H2" />
    </svg>
  );
}

/** Терапия — сердце. Прямо из перечня дизайн-передачи. */
export function IconHeart({ className }: IconProps) {
  return (
    <svg {...base} className={className}>
      <path d="M12 20s-7-4.35-7-9.5A3.5 3.5 0 0 1 12 8a3.5 3.5 0 0 1 7 2.5c0 5.15-7 9.5-7 9.5Z" />
    </svg>
  );
}

/** Ещё — три точки. Открывает лист с остальными пунктами. */
export function IconMore({ className }: IconProps) {
  return (
    <svg {...base} className={className}>
      <circle cx="5" cy="12" r="1.4" />
      <circle cx="12" cy="12" r="1.4" />
      <circle cx="19" cy="12" r="1.4" />
    </svg>
  );
}

/** Безопасность — щит. */
export function IconShield({ className }: IconProps) {
  return (
    <svg {...base} className={className}>
      <path d="M12 3l7 3v5.5c0 4.4-3 8-7 9.5-4-1.5-7-5.1-7-9.5V6l7-3Z" />
    </svg>
  );
}

/** Клуб — круг людей, а не «сообщество» абстракцией. */
export function IconClub({ className }: IconProps) {
  return (
    <svg {...base} className={className}>
      <circle cx="9" cy="9" r="3" />
      <path d="M3 19c0-2.8 2.7-5 6-5s6 2.2 6 5" />
      <path d="M16 6.2A3 3 0 0 1 16 12M17 14.5c2.4.6 4 2.3 4 4.5" />
    </svg>
  );
}

/** Поддержка — сообщение. */
export function IconSupport({ className }: IconProps) {
  return (
    <svg {...base} className={className}>
      <path d="M20 4H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h3v4l5-4h8a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2Z" />
    </svg>
  );
}

/** Светлое оформление — солнце. */
export function IconSun({ className }: IconProps) {
  return (
    <svg {...base} className={className}>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
    </svg>
  );
}

/** Тёмное оформление — месяц. */
export function IconMoon({ className }: IconProps) {
  return (
    <svg {...base} className={className}>
      <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z" />
    </svg>
  );
}

/** Что получаете — галочка. */
export function IconCheck({ className }: IconProps) {
  return (
    <svg {...base} className={className} width={18} height={18}>
      <path d="m4.5 12.5 5 5 10-11" />
    </svg>
  );
}

/** Чего не будет — крестик. */
export function IconCross({ className }: IconProps) {
  return (
    <svg {...base} className={className} width={18} height={18}>
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  );
}

/** Главная — дом с крышей. */
export function IconHome({ className }: IconProps) {
  return (
    <svg {...base} className={className}>
      <path d="M3 10.5 12 3l9 7.5V20a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1v-9.5Z" />
    </svg>
  );
}

/** Документы — лист с загнутым углом. */
export function IconDoc({ className }: IconProps) {
  return (
    <svg {...base} className={className}>
      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5Z" />
      <path d="M14 3v5h5M9 13h6M9 17h4" />
    </svg>
  );
}

/** Мой результат — искра. Единственный мотив с сайта, здесь линейный. */
export function IconSpark({ className }: IconProps) {
  return (
    <svg {...base} className={className}>
      <path d="M12 3c.6 4.4 2 5.8 6.4 6.4-4.4.6-5.8 2-6.4 6.4-.6-4.4-2-5.8-6.4-6.4C10 8.8 11.4 7.4 12 3Z" />
      <path d="M18 16.5c.3 1.8.9 2.4 2.7 2.7-1.8.3-2.4.9-2.7 2.7-.3-1.8-.9-2.4-2.7-2.7 1.8-.3 2.4-.9 2.7-2.7Z" />
    </svg>
  );
}

/**
 * Мой день — галочка в клетке.
 *
 * Не ещё один календарь: календарь уже занят семью днями наблюдения,
 * и две одинаковые иконки в одном меню женщина различать не обязана.
 * Здесь смысл другой — отмеченный день, а не расписание.
 */
export function IconDayCheck({ className }: IconProps) {
  return (
    <svg {...base} className={className}>
      <rect x="3" y="5" width="18" height="16" rx="3" />
      <path d="M8 3v4M16 3v4M3 10h18" />
      <path d="M9 15.5 11 17.5 15.5 13" />
    </svg>
  );
}

/** Задать вопрос — знак вопроса в облачке реплики. */
export function IconAsk({ className }: IconProps) {
  return (
    <svg {...base} className={className}>
      <path d="M21 12a8 8 0 0 1-8 8H8l-4 3v-4.5A8 8 0 0 1 5 5a8 8 0 0 1 16 7Z" />
      <path d="M10.2 9.7a2 2 0 0 1 3.8.9c0 1.3-2 1.6-2 3" />
      <path d="M12 16.3h.01" />
    </svg>
  );
}

/**
 * Иконки пунктов меню.
 *
 * Список ключей отсюда же становится типом, которым размечены сами
 * пункты: добавить пункт и забыть иконку теперь нельзя — не соберётся.
 * Раньше меню просто рисовало строку без значка, и две такие строки
 * выбивались из ряда, пока это не заметил человек.
 */
export const NAV_ICONS = {
  obs7: IconCalendar,
  passport14: IconDoc,
  program12: IconClub,
  consult222: IconAsk,
  intensive21: IconChart,
  lpf: IconHeart,
  more: IconMore,
  'CLUB-01': IconClub,
  SAFETY: IconShield,
  HOME: IconHome,
  LEGAL: IconDoc,
  SUPPORT: IconSupport,
  'MY-RESULT': IconSpark,
  'DAY-LOG': IconDayCheck,
  ASK: IconAsk,
} satisfies Record<string, (props: IconProps) => JSX.Element>;

/** Ключ пункта меню. Всё, у чего есть иконка, и ничего кроме. */
export type NavKey = keyof typeof NAV_ICONS;
